import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import { getPlans } from '../../services/storage';
import type { Plan } from '../../types';

export const SelectPlan = () => {
  const navigate = useNavigate();
  const { selectPlan } = useAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const plans = getPlans();

  // Verifica se algum plano tem desconto anual ativado
  const hasYearlyDiscount = plans.some(plan => plan.yearlyDiscountEnabled);

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
  };

  const handleContinue = () => {
    if (selectedPlan) {
      selectPlan(selectedPlan, billingCycle);
      navigate('/onboarding/pagamento');
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <>
      <Helmet>
        <title>Escolha seu Plano | Portal Ethics</title>
      </Helmet>
      
      <div className="min-h-screen bg-background-light py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-blue text-white font-semibold">
                  1
                </div>
                <div className="ml-4 text-sm font-medium text-neutral-900">Cadastro</div>
              </div>
              <div className="w-24 h-1 bg-primary-blue mx-4"></div>
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-blue text-white font-semibold">
                  2
                </div>
                <div className="ml-4 text-sm font-medium text-neutral-900">Plano</div>
              </div>
              <div className="w-24 h-1 bg-neutral-300 mx-4"></div>
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-neutral-200 text-neutral-500 font-semibold">
                  3
                </div>
                <div className="ml-4 text-sm font-medium text-neutral-500">Pagamento</div>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-neutral-900">
              Escolha seu plano
            </h2>
            <p className="mt-4 text-lg text-neutral-600">
              Selecione o plano ideal para sua empresa
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-full p-1 shadow-sm border border-neutral-200 inline-flex">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-primary-blue text-white'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Mensal
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'yearly'
                    ? 'bg-primary-blue text-white'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Anual
                {hasYearlyDiscount && (
                  <Badge variant="success" className="text-xs">-20%</Badge>
                )}
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {plans.map((plan: Plan) => (
              <Card
                key={plan.id}
                className={`relative p-8 transition-all cursor-pointer ${
                  selectedPlan === plan.id
                    ? 'ring-2 ring-primary-blue shadow-xl'
                    : 'hover:shadow-lg'
                } ${plan.isPopular ? 'border-primary-blue border-2' : ''}`}
                onClick={() => handleSelectPlan(plan.id)}
              >
                {plan.isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge variant="primary" className="flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Mais Popular
                    </Badge>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-neutral-900">{plan.name}</h3>
                  <p className="text-sm text-neutral-600 mt-2">{plan.description}</p>
                </div>

                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-extrabold text-neutral-900">
                      {formatPrice(billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice)}
                    </span>
                    <span className="text-neutral-500 ml-2">
                      /{billingCycle === 'monthly' ? 'mês' : 'ano'}
                    </span>
                  </div>
                {billingCycle === 'yearly' && plan.yearlyDiscountEnabled && (
                  <p className="text-sm text-success mt-1">
                    Economia de {formatPrice(plan.monthlyPrice * 12 - plan.yearlyPrice)}
                  </p>
                )}
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2 text-sm text-neutral-600">
                    <Check className="w-5 h-5 text-success flex-shrink-0" />
                    <span>Até {plan.maxUsers === 999999 ? 'usuários ilimitados' : `${plan.maxUsers} usuários`}</span>
                  </div>
                  {plan.features.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-neutral-600">
                      <Check className="w-5 h-5 text-success flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <Button
                  variant={selectedPlan === plan.id ? 'primary' : 'outline'}
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectPlan(plan.id);
                  }}
                >
                  {selectedPlan === plan.id ? 'Selecionado' : 'Selecionar'}
                </Button>
              </Card>
            ))}
          </div>

          {/* Continue Button */}
          <div className="flex justify-center">
            <Button
              variant="primary"
              size="lg"
              className="min-w-[200px]"
              disabled={!selectedPlan}
              onClick={handleContinue}
            >
              Continuar
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          <div className="mt-8 text-center">
            <Link to="/cadastro" className="text-sm text-neutral-600 hover:text-primary-blue">
              ← Voltar para cadastro
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
